import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AppConfig, APP_CONFIG } from '@app/app-config.module';

import { Observable } from 'rxjs';
import { timeout, retry } from 'rxjs/operators';
import { StandardResponse, StandardResponseList, StandardResponses } from '../models/standard-response.model';
import { DigitalMailboxRequestDtoDeleteMail, DigitalMailboxRequestDtoGet, DigitalMailboxRequestDtoGetCountMessage, DigitalMailboxRequestDtoGetFile, DigitalMailboxRequestDtoGetListReply, DigitalMailboxRequestDtoMarkAsRead, DigitalMailboxRequestDtoRestoreMail, DigitalMailboxRequestDtoSearch, DigitalMailboxRequestDtoSearchFavorite, DigitalMailboxRequestDtoSetFavorite, DigitalMailboxRequestDtoSetPassword, DigitalMailboxResponseDtoGet, DigitalMailboxResponseDtoSendToDocumentManagement, DigitalMailboxResponseDtoSetPassword, DigitalMailboxResposneDtoGetFile, DigitalMailboxResposneDtoGetProfileImage } from '../models/digital-mailbox.model';


@Injectable({
  providedIn: 'root'
})
export class DigitalMailboxService {
  constructor(
    @Inject(APP_CONFIG) private config: AppConfig,
    private http: HttpClient
  ) { }

  search(criteria: DigitalMailboxRequestDtoSearch,
    pageNumber: number,
    pageSize: number): Observable<StandardResponses<DigitalMailboxResponseDtoGet>> {
    return this.http
      .post<StandardResponses<DigitalMailboxResponseDtoGet>>(`${this.config.apiEndpoint}/digitalmailbox/search`, {
        criteria: criteria,
        pageNumber: pageNumber,
        pageSize: pageSize,
      })
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  searchFavorite(criteria: DigitalMailboxRequestDtoSearchFavorite,
    pageNumber: number,
    pageSize: number): Observable<StandardResponses<DigitalMailboxResponseDtoGet>> {
    return this.http
      .post<StandardResponses<DigitalMailboxResponseDtoGet>>(`${this.config.apiEndpoint}/digitalmailbox/search-favorite`, {
        criteria: criteria,
        pageNumber: pageNumber,
        pageSize: pageSize,
      })
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  getDetail(request: DigitalMailboxRequestDtoGet): Observable<StandardResponse<DigitalMailboxResponseDtoGet>> {
    return this.http
      .post<StandardResponse<DigitalMailboxResponseDtoGet>>(`${this.config.apiEndpoint}/digitalmailbox/get-detail`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  setPassword(request: DigitalMailboxRequestDtoSetPassword): Observable<StandardResponse<DigitalMailboxResponseDtoSetPassword>> {
    return this.http
      .post<StandardResponse<DigitalMailboxResponseDtoSetPassword>>(`${this.config.apiEndpoint}/digitalmailbox/set-password`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  sendDocument(request: DigitalMailboxRequestDtoGet): Observable<StandardResponse<DigitalMailboxResponseDtoSendToDocumentManagement>> {
    return this.http
      .post<StandardResponse<DigitalMailboxResponseDtoSendToDocumentManagement>>(`${this.config.apiEndpoint}/digitalmailbox/send-document-management`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  downloadFile(
    request: DigitalMailboxRequestDtoGet
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(
      `${this.config.apiEndpoint}/digitalmailbox/download-attach-file`,
      request,
      {
        responseType: 'blob',
        observe: 'response',
      }
    );
  }


  sendEmail(request: FormData): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/send-email`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  deleteEmail(request: DigitalMailboxRequestDtoDeleteMail): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/trash`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  getListReply(request: DigitalMailboxRequestDtoGetListReply): Observable<StandardResponseList<DigitalMailboxResponseDtoGet>> {
    return this.http
      .post<StandardResponseList<DigitalMailboxResponseDtoGet>>(`${this.config.apiEndpoint}/digitalmailbox/get-list-reply`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  setFavorite(request : DigitalMailboxRequestDtoSetFavorite): Observable<StandardResponses<String>> {
    return this.http
      .post<StandardResponses<String>>(`${this.config.apiEndpoint}/digitalmailbox/set-favorite`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  saveDraft(request: FormData): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/save-drafts`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }
  
  getCountMessage(): Observable<StandardResponseList<DigitalMailboxRequestDtoGetCountMessage>> {
    return this.http
      .post<StandardResponseList<DigitalMailboxRequestDtoGetCountMessage>>(`${this.config.apiEndpoint}/digitalmailbox/get-count-message`,{})
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  uploadProfilePicture(request: FormData): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/save-profile-picture`,request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  getProfileImage(): Observable<StandardResponse<DigitalMailboxResposneDtoGetProfileImage>> {
    return this.http
      .post<StandardResponse<DigitalMailboxResposneDtoGetProfileImage>>(`${this.config.apiEndpoint}/digitalmailbox/get-profile-picture`,{})
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  sendReplyEmail(request: FormData): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/reply-mail`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }
  
  getFile(request: DigitalMailboxRequestDtoGetFile): Observable<StandardResponse<DigitalMailboxResposneDtoGetFile>> {
    return this.http
      .post<StandardResponse<DigitalMailboxResposneDtoGetFile>>(`${this.config.apiEndpoint}/digitalmailbox/get-file`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  } 
  
  clearTrash(): Observable<StandardResponse<String>> {
    return this.http
      .get<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/clear-trash`)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout)); 
  }

  restoreMail(request: DigitalMailboxRequestDtoRestoreMail): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/restore`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  } 
  
  deleteProfilePicture(): Observable<StandardResponse<String>> {
    return this.http
      .get<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/deleted-profile-picture`)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout)); 
  }

  markAsRead(request : DigitalMailboxRequestDtoMarkAsRead): Observable<StandardResponses<String>> {
    return this.http
      .post<StandardResponses<String>>(`${this.config.apiEndpoint}/digitalmailbox/set-mark-as-read`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  sendToPrintAndPost(request): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/digitalmailbox/print-and-post`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }
  // 
}
